/* eslint-disable react/jsx-no-bind */
import { useNavigate, Outlet, useMatches } from "react-router-dom";
import { useCallback, useEffect } from "react";
import {
  StepsDisplayMode,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import FilterByDropdown from "~/components/build/Show/components/Sidebar/FilterByDropdown";
import { useMobileBreakpoint } from "~/components/build/Show/lib/useBreakpoints";
import { MobileStepsView } from "~/components/build/Show/components/MobileStepsView";
import { SubNavBar, SubNavLink } from "~/components/build/Show/components/SubNavLink";
import { applySearchParams } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { debounce } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { TableSearchInput } from "~/components/build/Show/components/TableView/TableSearchInput";
import { TableActionsDropdown } from "~/components/build/Show/components/TableView/TableActionsDropdown";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";

export function StepsIndexRedirect() {
  const currentModeId = useBuildPreferencesStore((state) => state.stepsDisplayMode);
  const params = useQueryParams();
  const navigate = useNavigate();

  useEffect(() => {
    let display: StepsDisplayMode;

    switch (currentModeId) {
      case StepsDisplayMode.Canvas:
        if (Features.DisableStepsCanvasView) {
          // Fall back to table view if the user has set Canvas as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          display = StepsDisplayMode.Table;
        } else {
          display = currentModeId;
        }
        break;
      case StepsDisplayMode.Table:
        display = currentModeId;
        break;
      case StepsDisplayMode.Waterfall:
        if (Features.DisableStepsWaterfallView) {
          // Fall back to table view if the user has set Waterfall as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          display = StepsDisplayMode.Table;
        } else {
          display = currentModeId;
        }
        break;
      default:
        // Fallback to canvas if unknown or not set
        display = StepsDisplayMode.Canvas;
    }

    navigate(applySearchParams(`./${display}`, params), { replace: true, flushSync: true });
  }, [currentModeId, params, navigate]);

  return null;
}

const displayModes = [
  { id: StepsDisplayMode.Canvas, label: "Canvas" },
  { id: StepsDisplayMode.Table, label: "Table" },
  { id: StepsDisplayMode.Waterfall, label: "Waterfall" },
];

export default function StepsLayout() {
  const params = useQueryParams();
  const isMobile = useMobileBreakpoint();
  const setStepsDisplayMode = useBuildPreferencesStore((state) => state.setStepsDisplayMode);
  const tableSearchValue = useBuildStore((state) => state.tableSearchValue);
  const setTableSearchValue = useBuildStore((state) => state.setTableSearchValue);

  const updateSearch = useCallback(
    debounce((inputValue: string) => {
      // If the value is the same as the current search, do nothing
      // This prevents unnecessary state updates and re-renders.
      if (inputValue === tableSearchValue) {
        return;
      }

      setTableSearchValue(inputValue);
    }, 250),
    [tableSearchValue, setTableSearchValue],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateSearch(value);
    },
    [updateSearch],
  );

  const matches = useMatches();
  const showTableSearch =
    Features.TableViewSearchField && ([...matches].pop()?.id === BuildView.Table || isMobile);
  const isTableView = [...matches].pop()?.id === BuildView.Table;

  return (
    <div className="flex min-w-0 flex-auto flex-col">
      <SubNavBar>
        <div className="hidden gap-2 md:!flex">
          {displayModes.map((mode) => (
            <SubNavLink
              to={applySearchParams(`./${mode.id}`, params)}
              key={mode.id}
              onClick={() => setStepsDisplayMode(mode.id)}
            >
              {mode.label}
            </SubNavLink>
          ))}
        </div>

        <section className="flex w-full items-center gap-2">
          <FilterByDropdown />

          {showTableSearch && <TableSearchInput onChange={handleSearchChange} />}

          {isTableView && !isMobile && <TableActionsDropdown />}
        </section>
      </SubNavBar>

      {isMobile ? (
        <div className="relative mt-1 flex-auto rounded-b-md bg-white">
          <MobileStepsView />
        </div>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
